import React, { useEffect, useMemo, useState } from "react";
import {
  convertSnakeToCamelCase,
  rgba2hex,
  useActiveCanvas,
} from "@/utils/utils";
import {
  Tabs as TabsUnstyled,
  TabsList as TabsListUnstyled,
  Tab as TabUnstyled,
  TabPanel as TabPanelUnstyled,
} from "@mui/base";
import {
  CSS_VALUES_TO_COLLECT,
  EXPANDABLE_CSS_VALUES,
  SCREEN_SIZE_NAME_TO_SIZE,
} from "@/utils/constants";
import TailwindStylingPanel from "./TailwindStylingPanel";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  selectMultiSelectedTempoElements,
  selectSelectedTempoElement,
  sendMessageToIFrame,
} from "@/store/iframeSlice";
import PropManager from "./PropManager";
import { convertExpandedDefinedStyles } from "@/utils/cssValueUtils";
import { TempoElement } from "@/utils/tempoElement";
import {
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/store/zustand/canvasStore";
import { X } from "lucide-react";
import useLayoutStore from "@/store/zustand/layoutStore";
import useModifiers, { ALL_MODIFIERS } from "@/hooks/useModifiers";
import useUpdateStoryboardSize from "../Canvas/hooks/useUpdateStoryboardSize";
import { ClassType } from "@/lib/tailwind/properties";
import useDebouncedState from "@/hooks/useDebouncedState";

// Matches tempo-devtools, used for instant updates
const TEMPO_INSTANT_UPDATE_STYLING_PREFIX = "arb89-styling-";
const MESSAGE_AGGREGATE_TIME_MS = 100;

interface Props {
  selectedTreeElement: any;

  cssElementLookup: any;

  applyChangeToElement: (
    styleChange: { [styleKey: string]: string },
    modifiers: string[],
    customProperties: ClassType[]
  ) => void;
}

// The interface that is passed into the tailwind styling panel
export interface CssFieldDefinition {
  cssName: string;
  value?: any;
  set?: boolean; // Whether the field is set in the codebase
  evaluatedValue?: string;
  cssSelectorSource?: string; // If this field was set from a particular css selector
  tailwindValueToDisplay?: string; // Parsed out value from the tailwind class (e.g. blue instead of #0000ff)
  isFromInstantUpdate?: boolean;
}

/**
 * CSS Rule returned from the iframes
 */
export interface CssRule {
  filename?: string;
  selector?: string;
  atrule?: string;
  codebaseId?: string;
  source?: any;
  styles?: { [cssKey: string]: string };
  inherited?: boolean;
  applied?: boolean; // Whether this css rule is applied to the current element
  removable?: boolean; // Whether this css rule is removable with a simple class change
}

const RightStyle: React.FC<Props> = (props) => {
  const selectedElement: TempoElement = useSelector(selectSelectedTempoElement);
  const multiselectedElements: TempoElement[] = useSelector(
    selectMultiSelectedTempoElements
  );

  // TODO: get rid of this and just track it from the backend?
  // This is the cache so that when a user makes a change it immediately shows in the styling panel
  const [styleSet, setStyleSet] = useState({});

  const iFrameMessageResponses = useSelector(
    (state: any) => state.iframe.iFrameMessageResponses
  );
  const extractedCssRules: CssRule[] = useSelector(
    (state: any) => state.iframe.processedCssRules
  );
  const cssEvals: { [cssName: string]: string } = useSelector(
    (state: any) => state.iframe.cssEvals
  );

  const { modifiers, addModifier, removeModifier, clearModifiers } =
    useModifiers([]);

  const iframeChangedKey = useSelector(
    (state: any) => state.iframe.iframeChangedKey
  );
  const forceReloadKey = useSelector(
    (state: any) => state.iframe.forceReloadKey
  );
  const iframeState = useSelector((state: any) => state.iframe.iframeState);
  const classList = useSelector((state: any) => state.iframe.elementClassList);

  const selectedNavTreeNode = useSelectedNavTreeNode();
  const multiselectedNavTreeNodes = useMultiselectedNavTreeNode();
  const collapseRight = useLayoutStore((state) => state.collapseRight);

  const dispatch = useDispatch<any>();

  const canvas = useActiveCanvas();

  const { setSize } = useUpdateStoryboardSize(canvas);

  const [previousWidths, setPreviousWidths] = useState<{
    [key: string]: number;
  }>({});

  const [lastActiveStoryboardId, setLastActiveStoryboardId] = useState(null);

  const [iframeValuesDebounced, setIFrameValuesDebounced] = useDebouncedState(
    {
      cssEvals,
      classList,
      extractedCssRules,
    },
    MESSAGE_AGGREGATE_TIME_MS
  );

  useEffect(() => {
    setIFrameValuesDebounced({ cssEvals, classList, extractedCssRules });
  }, [cssEvals, classList, extractedCssRules, setIFrameValuesDebounced]);

  useEffect(() => {
    // Identify if there is a size-related modifier
    const sizeModifier = modifiers.find((modifier) =>
      ALL_MODIFIERS.size.includes(modifier)
    );
    const currentStoryboardId = selectedElement?.storyboardId;
    const currentStoryboard = canvas?.storyboards?.find(
      (s) => s.id === currentStoryboardId
    );

    // Function to revert to previous sizes and clear the storage
    const revertAndClearPreviousWidths = () => {
      Object.entries(previousWidths).forEach(([storyboardId, width]) => {
        const storyboard = canvas?.storyboards.find(
          (s) => s.id === storyboardId
        );
        if (storyboard) {
          setSize(storyboard, width, storyboard.height);
        }
      });
      setPreviousWidths({});
    };

    // Always check for conditions to revert first
    if (
      !sizeModifier ||
      !currentStoryboard ||
      selectedElement.isEmpty() ||
      currentStoryboardId !== lastActiveStoryboardId
    ) {
      revertAndClearPreviousWidths();
    }

    if (sizeModifier && currentStoryboard && !selectedElement.isEmpty()) {
      // Setting new size
      const newSize = SCREEN_SIZE_NAME_TO_SIZE[sizeModifier];
      if (newSize) {
        // Store the current width before updating
        const currentWidth = currentStoryboard?.width;
        setPreviousWidths((prevWidths) => ({
          ...prevWidths,
          [currentStoryboardId]: currentWidth,
        }));

        // Apply the new size
        setSize(currentStoryboard, newSize.width, null);
        // @ts-expect-error TS2345
        setLastActiveStoryboardId(currentStoryboardId);
      }
    }
  }, [selectedElement.getKey(), modifiers]); // React to changes in selectedElement and modifiers

  useEffect(() => {
    if (!styleSet[selectedElement.getKey()]) {
      styleSet[selectedElement.getKey()] = {};
    }
  }, [selectedElement]);

  const [activeTab, setActiveTab] = useState("styles");

  // For inspecting state/props
  const [inspectElementRequestId, setInspectElementRequestId] = useState(null);
  const inspectElementResponse =
    // @ts-expect-error TS2538
    iFrameMessageResponses[inspectElementRequestId];

  useEffect(() => {
    // If the iframe totally reloaded, wait longer for it to refresh
    // TODO: If this is not enough time the props will never update
    setTimeout(() => {
      setInspectElementRequestId(null);
    }, 2000);
  }, [forceReloadKey, iframeChangedKey]);

  useEffect(() => {
    setInspectElementRequestId(null);
  }, [selectedElement]);

  /**
   * Get the props & state for the selected component
   */
  useEffect(() => {
    if (selectedElement.isEmpty() || inspectElementRequestId) {
      return;
    }

    const requestId = uuidv4();

    let payload = {};
    if (selectedNavTreeNode?.isComponent) {
      payload = {
        isComponent: true,
        componentName: props.selectedTreeElement?.componentName,
        tempoElementID: selectedElement.codebaseId,
      };
    } else {
      let firstComponentAboveThisElement = selectedNavTreeNode;
      while (
        firstComponentAboveThisElement &&
        !firstComponentAboveThisElement.isComponent
      ) {
        // @ts-expect-error TS2322
        firstComponentAboveThisElement = firstComponentAboveThisElement.parent;
      }

      payload = {
        isComponent: false,
        tempoElementID: selectedElement.codebaseId,
        componentName: props.selectedTreeElement?.componentName,
        componentElementId:
          firstComponentAboveThisElement?.tempoElement.codebaseId,
      };
    }

    dispatch(
      sendMessageToIFrame({
        id: requestId,
        storyboardId: selectedElement.storyboardId,
        type: "inspectElement",
        payload,
      })
    );

    // @ts-expect-error TS2345
    setInspectElementRequestId(requestId);
  }, [
    props.selectedTreeElement,
    selectedNavTreeNode,
    selectedElement,
    inspectElementRequestId,
    forceReloadKey,
  ]);

  /**
   * Clear modifiers if the selected element has changed
   */
  useEffect(() => {
    clearModifiers();
  }, [selectedElement]);

  /**
   * Process all the CSS Rules to get css files and known classes
   */
  useEffect(() => {
    if (selectedElement.isEmpty() || !props.cssElementLookup) {
      return;
    }

    const processCssRules = () => {
      dispatch(
        sendMessageToIFrame({
          type: "executeFunction",
          storyboardId: selectedElement.storyboardId,
          payload: {
            functionName: "processRulesForSelectedElement",
            args: [props.cssElementLookup, selectedElement.getKey()],
          },
        })
      );
    };

    processCssRules();
  }, [
    selectedElement,
    props.cssElementLookup,
    iframeState,
    iframeChangedKey,
    selectedNavTreeNode,
  ]);

  useEffect(() => {
    dispatch(
      sendMessageToIFrame({
        type: "executeFunction",
        storyboardId: selectedElement.storyboardId,
        payload: {
          functionName: "setModifiersForSelectedElement",
          args: [modifiers, selectedElement.getKey()],
        },
      })
    );
  }, [
    modifiers,
    selectedElement,
    props.cssElementLookup,
    iframeState,
    iframeChangedKey,
    selectedNavTreeNode,
  ]);

  /**
   * Fetch the latest css styles on any changes
   */
  useEffect(() => {
    if (selectedElement.isEmpty()) {
      return;
    }

    const refetchCssValues = () => {
      dispatch(
        sendMessageToIFrame({
          type: "executeFunction",
          storyboardId: selectedElement.storyboardId,
          payload: {
            functionName: "getCssEvals",
            args: [selectedElement.getKey()],
          },
        })
      );

      dispatch(
        sendMessageToIFrame({
          type: "executeFunction",
          storyboardId: selectedElement.storyboardId,
          payload: {
            functionName: "getElementClassList",
            args: [selectedElement.getKey()],
          },
        })
      );
    };

    refetchCssValues();
  }, [
    selectedElement,
    props.cssElementLookup,
    iframeState,
    iframeChangedKey,
    selectedNavTreeNode,
    modifiers,
  ]);

  /**
   * Clean the style set dict when the iframe is refreshed
   */
  useEffect(() => {
    setStyleSet({});
  }, [forceReloadKey, selectedElement]);

  /**
   * This use memo processes all the info taken from the iframe and converts
   * it into a format required by the styling panel
   */
  const {
    cssNameToFieldDefinition,
    parentCssEvals,
    modifierTally,
  }: {
    cssNameToFieldDefinition?: { [cssName: string]: CssFieldDefinition };
    parentCssEvals?: { [cssName: string]: string };
    modifierTally: { [modifier: string]: number };
  } = useMemo(() => {
    const { cssEvals, extractedCssRules, classList } = iframeValuesDebounced;

    const classListSet = new Set(
      (classList || []).map((cls) => cls.replace(/\\/g, ""))
    );

    // These are the css fields that have defined tailwind stylings
    const tailwindCssNameToFieldDefinition: {
      [cssName: string]: CssFieldDefinition & {
        priority: number;
      };
    } = {};

    const cssNameToInstantUpdateTs = {};

    const modifierTally = {};

    // First go through all the processed Rules and apply them in reverse order as they come in
    // order of specificity
    (extractedCssRules || [])
      .slice()
      .reverse()
      .filter((elem: any) => {
        // Tally up the modifiers
        if (elem["modifiers"] && Object.keys(elem["modifiers"]).length > 0) {
          Object.keys(elem["modifiers"]).forEach((modifier) => {
            if (elem["modifiers"][modifier]) {
              if (!modifierTally[modifier]) {
                modifierTally[modifier] = 0;
              }

              modifierTally[modifier]++;
            }
          });
        }

        if (modifiers.length === 0) {
          return true;
        }

        // If there are modifiers, only show the ones that are applied
        // Element has a modifiers object which is a dict of modifier name to boolean
        // If the element doesn't contain all the modifiers, don't show it
        return modifiers.every((modifier) => {
          if (!elem["modifiers"]) {
            return false;
          }

          return elem["modifiers"][modifier];
        });
      })
      .forEach((elem: any) => {
        if (!elem.applied) {
          return;
        }

        const selector = elem.selector;
        const classParsed = elem.classParsed;

        const selectorInClassList =
          selector.startsWith(".") &&
          classListSet.has(selector.substring(1).replace(/\\/g, ""));
        const parsedClassInClassList =
          classParsed && classListSet.has(classParsed);

        // We only care about styles that exist because of directly a relevant class, meaning they are a
        // single class name selector and that class is in the class list
        // This is how tailwind works and we assume these are tailwind styles
        if (!selectorInClassList && !parsedClassInClassList) {
          return;
        }

        Object.keys(elem["styles"]).forEach((cssName: string) => {
          // Special case, if the selector has TEMPO_INSTANT_UPDATE_STYLING_PREFIX in it, parse out the ms timestamp and
          // check if it is later than the one defined in the tailwind class, and always make it more important
          // than any other extracted rule, as the instant update classes will always be more important
          let msTimestamp = null;
          let selectorToUse = selector;

          if (
            Object.keys(elem["modifiers"] || {}).length > 0 &&
            elem.classParsed
          ) {
            selectorToUse = "." + elem.classParsed;
          }

          if (selector.includes(TEMPO_INSTANT_UPDATE_STYLING_PREFIX)) {
            const substringAfterPrefix = selector.substring(
              TEMPO_INSTANT_UPDATE_STYLING_PREFIX.length + 1
            ); // +1 to include the dot for the selector
            if (substringAfterPrefix) {
              // @ts-expect-error TS2322
              msTimestamp = new Number(
                substringAfterPrefix.split("-")[0] || "0"
              );
              selectorToUse =
                "." +
                substringAfterPrefix.substring(
                  substringAfterPrefix.indexOf("-") + 1
                );
            }
          }

          // Have found an instant update one already, compare timestamps
          if (cssNameToInstantUpdateTs[cssName]) {
            if (
              !msTimestamp ||
              msTimestamp < cssNameToInstantUpdateTs[cssName]
            ) {
              return;
            }
          } else {
            cssNameToInstantUpdateTs[cssName] = msTimestamp;
          }

          /**
           * Many classes/rules can update the same property.
           * Only 1 is responsible for the evaluated value.
           *
           * This is a best effort guess at which class is more granular.
           * If a Tailwind class is higher in scope (ex: `px-4`) it will likely mutate more keys.
           *
           * px-4 => [padding-right, padding-left]
           * pr-72 => [padding-right]
           *
           * In the case when both of these are in the class list, we priortize pr-72 because it is more specific.
           * This is also how Tailwind *tends* to work
           * See: https://tailwindcss.com/blog/automatic-class-sorting-with-prettier#how-classes-are-sorted
           */
          const numberOfStylesDefinedByClass = Object.keys(elem.styles).length;
          if (
            !tailwindCssNameToFieldDefinition[cssName] ||
            (selectorInClassList &&
              numberOfStylesDefinedByClass <
                tailwindCssNameToFieldDefinition[cssName].priority)
          ) {
            tailwindCssNameToFieldDefinition[cssName] = {
              cssName,
              value: elem["styles"][cssName],
              cssSelectorSource: selectorToUse,
              isFromInstantUpdate: !!msTimestamp,
              priority:
                numberOfStylesDefinedByClass > 0
                  ? numberOfStylesDefinedByClass
                  : Number.POSITIVE_INFINITY,
            };
          }
        });
      });

    // TODO: Fix this
    const styleSetForSelection = styleSet[selectedElement.getKey()] || {};

    const allRemovableClasses = new Set(
      props.selectedTreeElement?.removableClasses || []
    );

    // Now go through all needed CSS Names for the styling panel
    // If they are explicitly returned through css rules use the information from that
    // if not, get the value from the css evaluations
    const cssNameToFieldDefinition: { [cssName: string]: CssFieldDefinition } =
      {};
    CSS_VALUES_TO_COLLECT.forEach((cssName: string) => {
      cssNameToFieldDefinition[cssName] = {
        cssName,
      };

      // First, get the value and if it is directly set

      // This is the value in the cache, set it explicitly
      if (styleSetForSelection[cssName] !== undefined) {
        // From the style set dict (local cache for instant updates)
        cssNameToFieldDefinition[cssName].value = styleSetForSelection[cssName];

        // If null it means the value was deleted
        cssNameToFieldDefinition[cssName].set =
          styleSetForSelection[cssName] !== null;
      } else {
        cssNameToFieldDefinition[cssName].value = null;

        // For styles that can be set in different ways (e.g. padding-horizontal, padding, padding-left/right, etc)
        // Convert them out to their individual parts (top, right, bottom, left)
        let valueFromTailwind =
          tailwindCssNameToFieldDefinition[cssName]?.value;
        let cssEvalsStyling = cssEvals?.[cssName] || null;

        // If it is expandable, the try to get the value from the individual parts if those are defined
        // E.g. we can get padding-right from the padding value
        if (EXPANDABLE_CSS_VALUES.has(cssName)) {
          if (!valueFromTailwind) {
            valueFromTailwind = convertExpandedDefinedStyles(
              cssName,
              tailwindCssNameToFieldDefinition
            );
          }

          // We also expand out the css evals
          cssEvalsStyling = convertExpandedDefinedStyles(
            cssName,
            // @ts-expect-error TS2345
            null,
            cssEvals,
            valueFromTailwind
          );
        }

        // The selected rule comes directly from the codebase (or the element styles in the case of inline styles)
        if (!selectedElement.isEmpty() && valueFromTailwind) {
          cssNameToFieldDefinition[cssName].value = valueFromTailwind;
          // @ts-expect-error TS2322
          cssNameToFieldDefinition[cssName].evaluatedValue = cssEvalsStyling;
        } else {
          cssNameToFieldDefinition[cssName].value = cssEvalsStyling;
          // @ts-expect-error TS2322
          cssNameToFieldDefinition[cssName].evaluatedValue = cssEvalsStyling;
        }

        cssNameToFieldDefinition[cssName].set = valueFromTailwind !== undefined;

        // Always use the evaluated color because it will always be an rgba.
        if (cssName.includes("color")) {
          cssNameToFieldDefinition[cssName].value = rgba2hex(cssEvalsStyling);
          cssNameToFieldDefinition[cssName].evaluatedValue =
            rgba2hex(cssEvalsStyling);
        }
      }

      cssNameToFieldDefinition[cssName].cssSelectorSource =
        tailwindCssNameToFieldDefinition[cssName]?.cssSelectorSource?.replace(
          /\\/g,
          ""
        );

      // If this is a tailwind class and it is not in the list of removable classes, then don't say that it's set (don't allow deletions)
      if (
        !tailwindCssNameToFieldDefinition[cssName]?.isFromInstantUpdate && // If it's from instant update, allow it to be set always
        cssNameToFieldDefinition[cssName].set &&
        cssNameToFieldDefinition[cssName].cssSelectorSource &&
        /\.[a-z\-\.]*/.test(cssNameToFieldDefinition[cssName].cssSelectorSource)
      ) {
        cssNameToFieldDefinition[cssName].set = allRemovableClasses.has(
          cssNameToFieldDefinition[cssName].cssSelectorSource.substring(1)
        );
      }
    });

    const parentCssEvals: { [cssName: string]: string } = cssEvals?.[
      "parent"
    ] as unknown as { [cssName: string]: string };

    return { cssNameToFieldDefinition, parentCssEvals, modifierTally };
  }, [
    props.selectedTreeElement,
    styleSet,
    iframeChangedKey,
    modifiers,
    iframeValuesDebounced.classList,
    iframeValuesDebounced.cssEvals,
    iframeValuesDebounced.extractedCssRules,
  ]);

  const applyCssChanges = (
    changes: any,
    modifiers: string[],
    customProperties: ClassType[]
  ) => {
    if (Object.keys(changes).length !== 0) {
      setStyleSet({
        ...styleSet,
        [selectedElement.getKey()]: {
          ...styleSet[selectedElement.getKey()],
          ...changes,
        },
      });

      // Needed to set in react/programatically
      const convertedChanges = {};
      Object.keys(changes).forEach((cssProp) => {
        convertedChanges[convertSnakeToCamelCase(cssProp)] = changes[cssProp];
      });

      props.applyChangeToElement(convertedChanges, modifiers, customProperties);
    }
  };

  return (
    <div className="right-style hide-scrollbar">
      <TabsUnstyled
        defaultValue={"styles"}
        value={activeTab}
        onChange={(e: any, val: string) => val && setActiveTab(val)}
      >
        <TabsListUnstyled className="tab-list w-full">
          <TabUnstyled className="tab" value={"styles"}>
            Styles
          </TabUnstyled>
          <TabUnstyled className="tab" value={"props"}>
            Props
          </TabUnstyled>
          <div className="flex-grow flex flex-row justify-end items-center pr-4">
            <X
              className="w-4 h-4 cursor-pointer text-secondary-800 hover:text-secondary-900"
              onClick={() => collapseRight()}
            />
          </div>
        </TabsListUnstyled>
        <TabPanelUnstyled value={"styles"}>
          <TailwindStylingPanel
            cssValues={cssNameToFieldDefinition}
            parentCssEvals={parentCssEvals}
            applyCssChanges={applyCssChanges}
            selectedElement={selectedElement}
            multiselectedElements={multiselectedElements}
            classList={classList}
            selectedTreeElement={props.selectedTreeElement}
            modifiers={modifiers}
            addModifier={addModifier}
            removeModifier={removeModifier}
            modifierTally={modifierTally}
          />
        </TabPanelUnstyled>

        <TabPanelUnstyled value={"props"}>
          <PropManager
            inspectElementResponse={inspectElementResponse}
            selectedTreeElement={props.selectedTreeElement}
            // @ts-expect-error TS2322
            loadingProps={inspectElementRequestId && !inspectElementResponse}
          />
        </TabPanelUnstyled>
      </TabsUnstyled>
    </div>
  );
};

export default React.memo(RightStyle);
